/**
 * Pure helpers for the sidebar's sub data group navigation.
 *
 * The group payload (`/datagroups/{group}?releaseVersion=…`) returns every
 * entity with its full subgroup chain in `parent` ("Algorithm/Transaction"), so
 * selecting a subgroup is a filter over data the client already holds rather
 * than another fetch. These helpers hold no React state and perform no I/O.
 */

const asArray = (value) => (Array.isArray(value) ? value : []);

/** Join a chain into the `parent` string format, ignoring empty segments. */
export function chainToPath(chain) {
  return asArray(chain).filter(Boolean).join('/');
}

/**
 * Filter entities to those at or below a subgroup chain.
 *
 * Matching is inclusive of descendants — selecting "Algorithm" keeps everything
 * under "Algorithm/…" — because the dropdowns read as progressive filters. The
 * trailing slash on the prefix test is what stops "Test" from also matching
 * "TestResult".
 *
 * Returns the original array (same identity) for an empty chain so memoized
 * consumers do not re-render, matching `filterByName` in ./textSearch.
 *
 * @param {Array<{parent?: string}>} items
 * @param {string[]} chain
 * @returns {Array<object>}
 */
export function filterByChain(items, chain) {
  if (!Array.isArray(items)) return [];

  const path = chainToPath(chain);
  if (!path) return items;

  const prefix = `${path}/`;
  return items.filter((item) => {
    const parent = item?.parent;
    if (typeof parent !== 'string') return false;
    return parent === path || parent.startsWith(prefix);
  });
}

/**
 * The distinct subgroup names one level below `chain`, sorted.
 *
 * Derived from the entities themselves, so a node with no entities anywhere
 * beneath it does not appear — there would be nothing to show under it.
 *
 * @param {Array<{parent?: string}>} items
 * @param {string[]} chain
 * @returns {string[]}
 */
export function nextLevelOptions(items, chain) {
  const depth = asArray(chain).filter(Boolean).length;
  const scoped = filterByChain(items, chain);
  const options = new Set();

  for (const item of asArray(scoped)) {
    const parent = item?.parent;
    if (typeof parent !== 'string') continue;
    const segment = parent.split('/').filter(Boolean)[depth];
    if (segment) options.add(segment);
  }

  return [...options].sort((a, b) => a.localeCompare(b));
}

/**
 * Deepest `parent` chain present, so the number of rendered dropdowns follows
 * the data instead of assuming a fixed nesting depth.
 *
 * @param {Array<{parent?: string}>} items
 * @returns {number}
 */
export function maxDepth(items) {
  let depth = 0;

  for (const item of asArray(items)) {
    const parent = item?.parent;
    if (typeof parent !== 'string') continue;
    const segments = parent.split('/').filter(Boolean).length;
    if (segments > depth) depth = segments;
  }

  return depth;
}
